import { JSX } from 'react';
import { Area } from 'recharts';
import type { Component } from '../../../api/pages-client/pages-client.type';
import type { TransformedTarget, TransformedTargetThreshold } from '../../../api/targets-client/targets-client.type';
import { TargetComparison } from '../../../api/targets-client/targets-client.type';
import i18n from '../../../base-dictionary';
import { newCOLORS } from '../../../styles/colors';
import { secondaryBase } from '../../../styles/design-tokens';
import { getTargetFill, getTargetStrokeDashArray } from '../../adherence/targets/targets.helpers';
import { TARGET_THRESHOLD_LABELS, TargetThreshold } from '../../adherence/targets/targets.type';
import { getComponentDateRange } from '../dashboards.helpers';
import { COLORS } from '../dashboards.type';

/**
 * Generates reference lines and areas for target visualization in recharts charts
 * @param {TransformedTarget} target - The transformed target object containing comparison type and time allocations
 * @returns {JSX.Element | null} JSX elements for reference lines/areas or null if invalid target values
 */
const getTargetAreaChart = (target: TransformedTarget): JSX.Element => {
  const targetFillColor = getTargetFill(target.target_comparison, secondaryBase);
  const targetStrokeDashArray = getTargetStrokeDashArray(target.target_comparison);
  const targetBase =
    target.target_comparison === TargetComparison.GTE || target.target_comparison === TargetComparison.GT
      ? 'dataMax'
      : 'dataMin';
  return (
    <Area
      key={`target-area-${target.id}`}
      type="monotone"
      dataKey={`target_${target.id}`}
      name={`${target.name} (Target)`}
      stroke={secondaryBase}
      strokeDasharray={targetStrokeDashArray}
      fill={targetFillColor}
      dot={false}
      baseValue={targetBase}
    />
  );
};

/**
 * Calculates the average and total values for a target, considering the target comparison type
 * @param {TransformedTarget} target - The transformed target object containing comparison type and time allocations
 * @returns {Object} An object with average_lower, average_upper, total_lower, and total_upper values
 */
const getProratedAverageTotals = (
  target: TransformedTarget,
): { average_lower: number; average_upper?: number; total_lower: number; total_upper?: number } => {
  if (
    target.target_comparison === TargetComparison.RANGE_EX ||
    target.target_comparison === TargetComparison.RANGE_IN
  ) {
    const values = Object.values(target.time_allocations);
    const lower = values.map((value) =>
      typeof value === 'object' && value !== null && 'value_lower' in value ? value.value_lower : 0,
    );
    const upper = values.map((value) =>
      typeof value === 'object' && value !== null && 'value_upper' in value ? value.value_upper : 0,
    );
    const totalLower = lower.reduce((acc, curr) => acc + curr, 0);
    const totalUpper = upper.reduce((acc, curr) => acc + curr, 0);
    const averageLower = lower.length ? Number((totalLower / lower.length).toFixed(2)) : 0;
    const averageUpper = upper.length ? Number((totalUpper / upper.length).toFixed(2)) : 0;
    return {
      average_lower: averageLower,
      average_upper: averageUpper,
      total_lower: Number(totalLower.toFixed(2)),
      total_upper: Number(totalUpper.toFixed(2)),
    };
  }
  const values = Object.values(target.time_allocations).map((value) => (typeof value === 'number' ? value : 0));
  const total = Number(values.reduce((acc, curr) => acc + curr, 0).toFixed(2));
  const average = values.length ? Number((total / values.length).toFixed(2)) : 0;
  return {
    average_lower: average,
    total_lower: total,
  };
};

/**
 * Generates a link to the detailed view for a dashboard component
 * @param {Component} component - The dashboard component to generate a link for
 * @returns {string | null} The URL to the component's detailed view, or null if no link can be generated
 *
 * This function creates navigation links based on the component's object type:
 * - For initiatives: Links to the strategy view
 * - For portfolios, projects, and boards: Links to process analysis with appropriate filters
 * - The link includes date range parameters from the component's configuration
 */
const getComponentDetailsLink = (component: Component, portfolioId: string | undefined): string | null => {
  const componentData = component.component_data?.[0];
  if (!componentData) return null;
  const { startDate, endDate } = getComponentDateRange(componentData);
  const { object_type, object_ids } = componentData;
  const baseProcessAnalysisLink = `/application/process-analysis?startDate=${startDate}&endDate=${endDate}&showSprintComparison=false`;
  switch (object_type) {
    case 'initiative':
      if (portfolioId && object_ids.length > 0) {
        return `/application/strategy/${portfolioId}/${object_ids[0]}`;
      }
      return null;
    case 'portfolio':
      return `${baseProcessAnalysisLink}&portfolioIds=${object_ids.join(',')}&activeTab=portfolios`;
    case 'project':
      return `${baseProcessAnalysisLink}&teamIds=${object_ids.join(',')}&activeTab=teams`;
    case 'board':
      return `${baseProcessAnalysisLink}&boardIds=${object_ids.join(',')}&activeTab=boards`;
    case 'sprint':
      if (object_ids.length > 0) {
        return `/application/process-analysis?workPeriodBoardId=${object_ids[0]}&activeTab=work_periods&showSprintComparison=true`;
      }
      return null;
    default:
      return null;
  }
};

/**
 * Returns the default color for a given object based on its index
 * @param {string} objectId - The ID of the object
 * @param {Record<string, string>} colorMap - The color map for the object
 * @param {number} [index] - The index of the object
 * @returns {string} The default color for the object
 */
const getDefaultObjectColor = (objectId: string, colorMap: Record<string, string>, index?: number): string => {
  const colorKeys = Object.keys(COLORS);

  if (index !== undefined) {
    return colorMap[objectId] || COLORS[colorKeys[index % colorKeys.length] as keyof typeof COLORS].stroke;
  }
  return colorMap[objectId] || newCOLORS.darkPurple;
};

/**
 * Generates a human-readable message describing a target threshold for a specific date
 *
 * This function creates descriptive text for target thresholds (green, yellow, red) by:
 * - Retrieving the threshold bounds (lower/upper) for the specified date
 * - Formatting the message based on whether both bounds exist, or just one
 * - Using localized labels for threshold colors and terminology
 *
 * @param {TransformedTarget} target - The target object containing threshold data
 * @param {TargetThreshold} targetThreshold - The threshold type (green, yellow, or red)
 * @param {string} dateKey - The date key to look up threshold values for
 * @returns {string} A formatted message describing the threshold range or bound
 *
 * @example
 * // Returns: "Within Green threshold: 80 - 100"
 * getTargetThresholdMessage(target, TargetThreshold.GREEN, '2024-01-01')
 *
 * @example
 * // Returns: "Above Yellow threshold: 50"
 * getTargetThresholdMessage(target, TargetThreshold.YELLOW, '2024-01-01')
 */

const getTargetThresholdMessage = (
  transformedTargetThreshold: TransformedTargetThreshold,
  targetThreshold: TargetThreshold,
): string => {
  const lower = transformedTargetThreshold?.[`${targetThreshold}_threshold_lower`];
  const upper = transformedTargetThreshold?.[`${targetThreshold}_threshold_upper`];
  return lower && upper
    ? `Within ${TARGET_THRESHOLD_LABELS[targetThreshold]} ${i18n.t('targets.threshold')}: ${lower} - ${upper}`
    : lower
      ? `Above ${TARGET_THRESHOLD_LABELS[targetThreshold]} ${i18n.t('targets.threshold')}: ${lower} `
      : upper
        ? `Below ${TARGET_THRESHOLD_LABELS[targetThreshold]} ${i18n.t('targets.threshold')}: ${upper}`
        : '';
};

export {
  getComponentDetailsLink,
  getDefaultObjectColor,
  getProratedAverageTotals,
  getTargetAreaChart,
  getTargetThresholdMessage,
};
